import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { HelpProvider, HelpSlidePanel } from './HelpPanel';
import {
  Search,
  LayoutGrid,
  FileText,
  Users,
  Calendar,
  LogOut,
  Target,
  Mail,
  Upload,
  Award,
  Settings,
  Command,
  Eye,
} from 'lucide-react';

const JAMES_PHOTO = 'https://customer-assets.emergentagent.com/job_emergent-bd/artifacts/na85p4o5_F45B653F-CAB2-4295-806B-80D698ABDB3D%202.jpg';
// Optional per-instance client logo (sidebar bottom). Env-driven: unset -> slot renders nothing.
const CLIENT_LOGO_URL = process.env.REACT_APP_CLIENT_LOGO_URL;

const BRAND_LOGOS = [
  { src: '/brand-flip-breach.webp', alt: 'BREACH — IN DEPTH' },
  { src: '/brand-flip-intelligence.webp', alt: 'IN DEPTH Intelligence' },
];

const usePrefersReducedMotion = () => {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = React.useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches
  );
  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const handler = (e) => setReduced(e.matches);
    mq.addEventListener ? mq.addEventListener('change', handler) : mq.addListener(handler);
    return () => (mq.removeEventListener ? mq.removeEventListener('change', handler) : mq.removeListener(handler));
  }, []);
  return reduced;
};

// Top-left brand mark: slow, subtle sequential fade. Current logo holds 5s, fades fully
// out to blank, then the next logo fades in (never overlapping). Honours prefers-reduced-motion.
const RotatingBrandLogo = () => {
  const reduced = usePrefersReducedMotion();
  const [idx, setIdx] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const FADE = 1000;

  React.useEffect(() => {
    if (reduced) { setVisible(true); return undefined; }
    // preload both so the swap while blank is instant (no flash)
    BRAND_LOGOS.forEach((l) => { const im = new Image(); im.src = l.src; });
    const HOLD = 5000, BLANK = 300;
    const timers = [];
    const loop = () => {
      timers.push(setTimeout(() => {
        setVisible(false); // fade fully out
        timers.push(setTimeout(() => {
          setIdx((p) => (p + 1) % BRAND_LOGOS.length); // swap while blank
          timers.push(setTimeout(() => {
            setVisible(true); // fade the new logo in
            // sync signal: fires exactly when the mark animates in a new logo, so the
            // client OAD mark glows on the same beat / cadence as the top-left mark.
            window.dispatchEvent(new CustomEvent('oad-brand-cycle'));
            timers.push(setTimeout(loop, FADE)); // hold starts once fade-in completes
          }, BLANK));
        }, FADE));
      }, HOLD));
    };
    loop();
    return () => timers.forEach(clearTimeout);
  }, [reduced]);

  const logo = BRAND_LOGOS[idx];
  return (
    <div className="relative w-full" style={{ aspectRatio: '2000 / 700' }} data-testid="sidebar-brand-logo">
      <img
        src={logo.src}
        alt={logo.alt}
        className="absolute inset-0 w-full h-full object-contain"
        style={{
          opacity: reduced ? 1 : (visible ? 1 : 0),
          transition: reduced ? undefined : `opacity ${FADE}ms ease-in-out`,
        }}
        data-testid="sidebar-brand-logo-current"
      />
    </div>
  );
};

// Per-instance CLIENT mark (bottom of sidebar). The white OAD letters carry a slow turquoise
// glow (house teal #00D4C8) that fades in and out — the glyphs glow, not a block. Each pulse
// is triggered by the 'oad-brand-cycle' event dispatched by RotatingBrandLogo, so OAD glows on
// exactly the same cadence / beat as the top-left mark (and once on mount, to start together).
const CLIENT_TEAL = '0, 212, 200'; // #00D4C8
const CLIENT_GLOW_MS = 1150;       // slow rise, then equal fall (~2.3s round trip == mark's fade window)
const ClientBrandLogo = ({ src }) => {
  const reduced = usePrefersReducedMotion();
  const [glow, setGlow] = React.useState(false);
  const offTimer = React.useRef(null);

  React.useEffect(() => {
    if (reduced) return undefined;
    const pulse = () => {
      if (offTimer.current) clearTimeout(offTimer.current);
      setGlow(true);
      offTimer.current = setTimeout(() => setGlow(false), CLIENT_GLOW_MS);
    };
    window.addEventListener('oad-brand-cycle', pulse);
    pulse(); // initial glow on mount — starts together with the top-left mark
    return () => {
      window.removeEventListener('oad-brand-cycle', pulse);
      if (offTimer.current) clearTimeout(offTimer.current);
    };
  }, [reduced]);

  const glowFilter = glow
    ? `drop-shadow(0 0 5px rgba(${CLIENT_TEAL}, 0.85)) drop-shadow(0 0 13px rgba(${CLIENT_TEAL}, 0.5))`
    : `drop-shadow(0 0 0 rgba(${CLIENT_TEAL}, 0))`;

  return (
    <img
      src={src}
      alt="Client logo"
      className="h-[84px] w-auto max-w-full object-contain opacity-90"
      style={
        reduced
          ? { filter: `drop-shadow(0 0 4px rgba(${CLIENT_TEAL}, 0.35))` }
          : { filter: glowFilter, transition: `filter ${CLIENT_GLOW_MS}ms ease-in-out`, willChange: 'filter' }
      }
      data-testid="sidebar-client-logo"
    />
  );
};

const Layout = ({ children, onOpenCommandPalette }) => {
  const { user, logout, isViewer, isTester } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutGrid, label: 'Dashboard' },
    { to: '/research', icon: Search, label: 'Research Engine' },
    { to: '/targets', icon: Users, label: 'Targets' },
    { to: '/pipeline', icon: Target, label: 'Pipeline' },
    { to: '/outreach', icon: Mail, label: 'Outreach' },
    { to: '/context-import', icon: Upload, label: 'Import Context' },
    { to: '/grants', icon: Award, label: 'Opportunities', testId: 'nav-opportunities' },
    { to: '/events', icon: Calendar, label: 'Events', testId: 'nav-events' },
    { to: '/projects', icon: FileText, label: 'Projects' },
    // 2G — Settings (org profile / thinking model / admin) hidden from TESTER accounts.
    ...(!isTester ? [{ to: '/settings/org-profile', icon: Settings, label: 'Settings' }] : []),
  ];

  const isJames = user?.name?.toLowerCase() === 'james';

  return (
    <HelpProvider>
      <div className="min-h-screen bg-[#0A1628] flex">
        {/* Sidebar */}
        <aside className="w-64 fixed left-0 top-0 h-screen bg-[#0E1C33] border-r border-[#1A2F4C] flex flex-col z-20">
          {/* Logo — rotating BREACH ⇄ IN DEPTH Intelligence brand mark */}
          <div className="p-4 border-b border-[#1A2F4C] flex items-center justify-center">
            <RotatingBrandLogo />
          </div>

          {/* Quick BREACH button — hidden for Viewer */}
          {onOpenCommandPalette && !isViewer && (
            <div className="px-4 pt-3">
              <button
                onClick={onOpenCommandPalette}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0A1628] border border-[#1A2F4C] text-[#A3B5C7] hover:text-[#00D4C8] hover:border-[#00D4C8]/30 transition-all text-xs"
                data-testid="sidebar-quick-breach-btn"
              >
                <Command className="w-3.5 h-3.5" />
                <span>Quick BREACH</span>
                <span className="ml-auto text-[10px] text-[#A3B5C7]/40 font-mono">⌘K</span>
              </button>
            </div>
          )}
          {isViewer && (
            <div className="px-4 pt-3">
              <div className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 text-amber-400 text-xs">
                <Eye className="w-3.5 h-3.5" />
                <span>READ-ONLY ACCESS</span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 min-h-0 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-[#00D4C8]/10 text-[#00D4C8] border border-[#00D4C8]/20'
                      : 'text-[#A3B5C7] hover:bg-[#0A1628] hover:text-white'
                  }`
                }
                data-testid={item.testId || `nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* Optional per-instance client brand mark — env-driven; renders nothing when unset */}
          {CLIENT_LOGO_URL && (
            <div className="px-4 pb-4 pt-1 flex justify-center">
              <ClientBrandLogo src={CLIENT_LOGO_URL} />
            </div>
          )}

          {/* User */}
          <div className="p-4 border-t border-[#1A2F4C]">
            <div className="flex items-center gap-3 mb-4">
              {isJames ? (
                <img
                  src={JAMES_PHOTO}
                  alt="James"
                  className="w-10 h-10 rounded object-cover border-2 border-[#00D4C8]"
                  data-testid="profile-photo"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#00D4C8]/20 flex items-center justify-center text-[#00D4C8] font-medium">
                  {user?.name?.charAt(0) || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#A3B5C7] capitalize">{isViewer ? 'Viewer (Read-Only)' : isJames ? 'Founder' : user?.role}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg text-[#A3B5C7] hover:text-[#00D4C8] hover:bg-[#0A1628] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 ml-64">
          <div className="min-h-screen p-8">
            {children}
          </div>
        </main>

        {/* Help slide-out panel */}
        <HelpSlidePanel />
      </div>
    </HelpProvider>
  );
};

export default Layout;
